import { apiRequest } from "@/lib/api";

export type MarketplaceType =
  | "products"
  | "services"
  | "vehicles"
  | "real_estate";

export type MarketplaceItem = {
  promotion?: {
    sponsored: true;
    campaign_id: string;
    campaign_placement: string;
  };
  id: number;
  name: string;
  slug: string;
  reference_code: string | null;
  short_description: string;
  item_kind: "product" | "service";
  marketplace_type: MarketplaceType;
  business_type: string;
  price: string;
  promotional_price: string | null;
  current_price: string;
  is_featured: boolean;
  views_count: number | null;
  primary_image_url: string | null;
  category: string | null;
  subcategory: string | null;
  business_attributes: Record<string, unknown>;
  company: {
    id: number;
    trade_name: string;
    slug: string;
    city: string;
    state: string;
    district: string;
    is_verified: boolean;
  };
  public_path: string;
};

export type MarketplaceResponse = {
  count: number;
  categories: string[];
  subcategories: string[];
  facets: {
    brands?: string[];
    models?: string[];
    years?: number[];
    neighborhoods?: string[];
    property_types?: string[];
  };
  results: MarketplaceItem[];
};

// The URL and API use the same explicit filter contract. Unknown parameters are omitted.
export function normalizeMarketplaceParams(input: URLSearchParams): URLSearchParams {
  const output = new URLSearchParams();
  const type = input.get("type") ?? "";
  if (["products", "services", "vehicles", "real_estate"].includes(type)) output.set("type", type);
  for (const key of ["search", "city"]) {
    const value = input.get(key)?.trim();
    if (value) output.set(key, value);
  }
  const state = input.get("state")?.trim().toUpperCase();
  if (state && ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"].includes(state)) output.set("state", state);
  const sort = input.get("sort") ?? "";
  if (["recent", "most_viewed", "price_asc", "price_desc"].includes(sort)) output.set("sort", sort);
  for (const key of ["min", "max"]) {
    const raw = (input.get(key) ?? input.get(`${key}_price`))?.trim().replace(",", ".");
    if (raw && /^\d+(\.\d{1,2})?$/.test(raw) && Number(raw) <= 9999999999.99) output.set(key, String(Number(raw)));
  }
  if (["1", "true", "yes"].includes(input.get("featured") ?? "")) output.set("featured", "1");
  const limit = input.get("limit") ?? "";
  if (/^\d+$/.test(limit)) output.set("limit", String(Math.max(1, Math.min(Number(limit), 100))));
  const specific = type === "vehicles" ? ["brand", "model"] : type === "real_estate" ? ["neighborhood", "property_type"] : ["products", "services"].includes(type) ? ["category", "subcategory"] : [];
  for (const key of specific) {
    const value = input.get(key)?.trim();
    if (value) output.set(key, value);
  }
  const year = input.get("year") ?? "";
  if (type === "vehicles" && /^\d{4}$/.test(year) && Number(year) >= 1900 && Number(year) <= 2100) output.set("year", year);
  const transaction = input.get("transaction_type") ?? "";
  if (type === "real_estate" && ["sale", "rent"].includes(transaction)) output.set("transaction_type", transaction);
  return output;
}

export async function getMarketplaceItems(
  params?: URLSearchParams,
): Promise<MarketplaceResponse> {
  const query = params?.toString();

  return apiRequest<MarketplaceResponse>(
    "/catalog/public/marketplace/" +
      (query ? "?" + query : ""),
  );
}
